import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import Skeleton from 'app/shared/components/common/Skeleton';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPagePermissions from 'hocs/withPagePermissions';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  clearConstructionAction,
  getConstructionBudgetAction,
  getDrawRequestsAction,
} from 'store/construction/actions';
import {
  selectConstructionBudget,
  selectConstructionBudgetLines,
  selectCurrentDrawRequest,
  selectDrawRequests,
} from 'store/construction/selectors';
import {
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
} from 'store/loanApplications/selectors';

import BudgetEditDrawer from './BudgetEditDrawer';
import BudgetTableSection from './components/BudgetTableSection';
import ConstructionSummarySection from './components/ConstructionSummarySection';
import DrawListSection from './components/DrawListSection';
import { wrapperStyles } from './Construction.styles';
import { ConstructionTestIds } from './Construction.testIds';
import { getHasActiveDrawRequest } from './Construction.utils';
import CreateDrawRequestDrawer from './CreateDrawRequestDrawer';
import DrawReviewDrawer from './DrawReviewDrawer';

const Construction = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [isError, setIsError] = useState(false);
  const [reviewDrawRequestId, setReviewDrawRequestId] = useState(null);
  const [isBudgetEditorOpen, setIsBudgetEditorOpen] = useState(false);
  const [isCreateDrawOpen, setIsCreateDrawOpen] = useState(false);

  const budget = useSelector(selectConstructionBudget(applicationId));
  const budgetLines = useSelector(selectConstructionBudgetLines(applicationId));
  const drawRequests = useSelector(selectDrawRequests(applicationId));
  const currentDrawRequest = useSelector(selectCurrentDrawRequest(applicationId));
  const hasActiveDrawRequest = useMemo(() => getHasActiveDrawRequest(drawRequests), [drawRequests]);

  const { isEditAllowed: isConstructionEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.CONSTRUCTION),
  );
  const isLoanEditAllowed = useSelector(selectIsLoanApplicationEditAllowed);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isEditAllowed = isConstructionEditAllowed && isLoanEditAllowed && !isLoanCanceled;

  const fetchData = useCallback(() => {
    if (!applicationId) {
      return;
    }

    setIsError(false);
    setIsLoading(true);

    Promise.all([
      dispatch(getConstructionBudgetAction({ applicationId })),
      dispatch(getDrawRequestsAction({ applicationId })),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, setIsLoading]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  useEffect(
    () => () => {
      dispatch(clearConstructionAction({ applicationId }));
    },
    [dispatch, applicationId],
  );

  const onReviewDraw = useCallback(drawRequestId => {
    setReviewDrawRequestId(drawRequestId);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setReviewDrawRequestId(null);
  }, []);

  const onAddDrawRequest = useCallback(() => {
    setIsCreateDrawOpen(true);
  }, []);

  const onCloseCreateDraw = useCallback(() => {
    setIsCreateDrawOpen(false);
  }, []);

  const onDrawCreated = useCallback(
    createdId => {
      fetchData();

      if (createdId) {
        setReviewDrawRequestId(createdId);
      }
    },
    [fetchData],
  );

  const onEditBudget = useCallback(() => {
    setIsBudgetEditorOpen(true);
  }, []);

  const onCloseBudgetEditor = useCallback(() => {
    setIsBudgetEditorOpen(false);
  }, []);

  return (
    <>
      {isLoading ? (
        <Skeleton length={3} type={SKELETON_TYPES.CARD} />
      ) : (
        <ErrorHandlingLayout isError={isError} onClick={fetchData}>
          <Box data-testid={ConstructionTestIds.WRAPPER} sx={wrapperStyles}>
            <ConstructionSummarySection budget={budget} budgetLines={budgetLines} />
            <BudgetTableSection
              budgetLines={budgetLines}
              isEditAllowed={isEditAllowed}
              onEditBudget={onEditBudget}
            />
            <DrawListSection
              currentDrawRequest={currentDrawRequest}
              drawRequests={drawRequests}
              hasBudget={!!budget}
              isAddDrawRequestDisabled={!isEditAllowed || hasActiveDrawRequest}
              isEditAllowed={isEditAllowed}
              onAddDrawRequest={onAddDrawRequest}
              onEditBudget={onEditBudget}
              onReviewDraw={onReviewDraw}
            />
          </Box>
        </ErrorHandlingLayout>
      )}

      <DrawReviewDrawer
        applicationId={applicationId}
        drawRequestId={reviewDrawRequestId}
        isEditAllowed={isEditAllowed}
        open={!!reviewDrawRequestId}
        onClose={onCloseDrawer}
        onDecided={fetchData}
      />

      <BudgetEditDrawer
        applicationId={applicationId}
        budget={budget}
        budgetLines={budgetLines}
        open={isBudgetEditorOpen}
        onClose={onCloseBudgetEditor}
        onSaved={fetchData}
      />

      <CreateDrawRequestDrawer
        applicationId={applicationId}
        budgetLines={budgetLines}
        open={isCreateDrawOpen}
        onClose={onCloseCreateDraw}
        onCreated={onDrawCreated}
      />
    </>
  );
};

Construction.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(
  withPagePermissions(PAGE_CODES.CONSTRUCTION),
  withLoading,
  memo,
)(Construction);
